<template>
    <div v-if="comic != null" id="comic" class="py-sm-4">
        <div class="card">
            <div class="card-header">
                <span class="fas fa-book fa-fw"></span>
                {{ comic.title }}
                <a v-if="comic.id !== null" :href="reader.BASE_URL + 'admin/comics/' + comic.slug" target="_blank">
                    <span class="fas fa-edit fa-fw me-0" aria-hidden="true" title="Edit"></span>
                </a>
                <button type="button" class="mc-follow-btn" :class="{ 'is-followed': isFollowed(comic.slug) }" :aria-pressed="isFollowed(comic.slug) ? 'true' : 'false'" @click="toggleFollow" :title="isFollowed(comic.slug) ? 'Ne plus suivre cette œuvre' : 'Suivre cette œuvre pour retrouver ses nouveautés'">
                    <span :class="(isFollowed(comic.slug) ? 'fas' : 'far') + ' fa-bookmark fa-fw'" aria-hidden="true"></span>
                    {{ isFollowed(comic.slug) ? 'Suivi' : 'Suivre' }}
                </button>
            </div>
            <div class="card-body">
                <div class="row">
                    <div class="col-xl-3 col-md-4 text-center">
                        <div class="thumbnail-full float-lg-start">
                            <img :src="comic.thumbnail" class="rounded" :alt="comic.title" :title="comic.title">
                        </div>
                    </div>
                    <div class="col-xl-9 col-md-8">

                        <div v-if="comic.last_chapter != null" class="row border-bottom py-1">
                            <div class="col-md-2 fw-bold">
                                <label class="mb-0">{{ reader.__('Last chapter') }}:</label>
                            </div>
                            <div class="col-md-10 text-success">
                                <span class="fas fa-book-open fa-fw" aria-hidden="true" title="Last chapter"></span>
                                <router-link :to="comic.last_chapter.url" class="text-success fw-bold">
                                    {{ comic.last_chapter.full_title }}
                                </router-link>
                            </div>
                        </div>

                        <div v-if="comic.alt_titles.length" class="row border-bottom py-1">
                            <div class="col-md-2 fw-bold">
                                <label class="mb-0 label-badge">{{ reader.__('Alternative titles') }}:</label>
                            </div>
                            <div class="col-md-10">
                                <template v-for="alt_title in comic.alt_titles">
                                    <span class="badge bg-success p-2 text-white">{{ alt_title }}</span>&nbsp;
                                </template>
                            </div>
                        </div>

                        <div v-if="comic.author" class="row border-bottom py-1">
                            <div class="col-md-2 fw-bold">
                                <label class="mb-0">{{ reader.__('Author') }}:</label>
                            </div>
                            <div class="col-md-10">
                                {{ comic.author }}
                            </div>
                        </div>

                        <div v-if="comic.artist" class="row border-bottom py-1">
                            <div class="col-md-2 fw-bold">
                                <label class="mb-0">{{ reader.__('Artist') }}:</label>
                            </div>
                            <div class="col-md-10">
                                {{ comic.artist }}
                            </div>
                        </div>

                        <div v-if="comic.target" class="row border-bottom py-1">
                            <div class="col-md-2 fw-bold">
                                <label class="mb-0 label-badge">{{ reader.__('Target') }}:</label>
                            </div>
                            <div class="col-md-10">
                                <router-link v-if="comic.target != null" :to="'/targets/' + comic.target.toLowerCase()"
                                             class="badge bg-info p-2 text-white">{{ comic.target }}
                                </router-link>
                                <template v-else>{{ comic.target }}</template>
                            </div>
                        </div>

                        <div v-if="comic.genres.length" class="row border-bottom py-1">
                            <div class="col-md-2 fw-bold">
                                <label class="mb-0 label-badge">{{ reader.__('Genres') }}:</label>
                            </div>
                            <div class="col-md-10">
                                <template v-for="genre in comic.genres">
                                    <router-link :to="'/genres/' + genre.slug" class="badge bg-info p-2 text-white">
                                        {{ genre.name }}
                                    </router-link>&nbsp;
                                </template>
                            </div>
                        </div>

                        <div class="row border-bottom py-1">
                            <div class="col-md-2 fw-bold">
                                <label class="mb-0">{{ reader.__('Format') }}:</label>
                            </div>
                            <div class="col-md-10">
                                {{ comic.format_id === 1 ? 'Manga' : 'Long Strip (Web Toons)' }}
                            </div>
                        </div>

                        <div class="row border-bottom py-1">
                            <div class="col-md-2 fw-bold">
                                <label class="mb-0">{{ reader.__('Adult') }}:</label>
                            </div>
                            <div class="col-md-10">
                                {{ comic.adult ? 'Yes' : 'No' }}
                            </div>
                        </div>

                        <div class="row border-bottom py-1">
                            <div class="col-md-2 fw-bold">
                                <label class="mb-0">{{ reader.__('Stats') }}:</label>
                            </div>
                            <div class="col-md-10">
                                <ul class="list-inline my-0">
                                    <li class="list-inline-item">
                                        <div class="mc-rating" v-if="comic">
                                            <div class="mc-stars" role="radiogroup" aria-label="Noter ce manga">
                                                <button v-for="n in 5" :key="n" type="button" class="mc-star" :class="{ 'is-on': starState(n) !== 'empty' }" @mouseenter="ratingHover = n" @mouseleave="ratingHover = 0" @click="sendRating(n)" :disabled="ratingBusy" :aria-label="'Noter ' + n + ' sur 5'">
                                                    <span :class="starIcon(n)"></span>
                                                </button>
                                            </div>
                                            <span class="mc-rating-text">
                                                <template v-if="ratingInfo && ratingInfo.average !== null">
                                                    <strong>{{ ratingLabel }}</strong><span class="mc-rating-count"> · {{ ratingInfo.count }} vote{{ ratingInfo.count > 1 ? 's' : '' }}</span>
                                                </template>
                                                <template v-else>Pas encore de note</template>
                                                <span v-if="ratingInfo && ratingInfo.your_vote" class="mc-rating-mine"> · Votre note : {{ ratingInfo.your_vote }}/5{{ ratingThanks ? ' — merci !' : '' }}</span>
                                                <span v-else class="mc-rating-hint"> · Cliquez pour noter</span>
                                            </span>
                                        </div>
                                    </li>
                                    <li class="list-inline-item text-info">
                                        <span class="fas fa-eye fa-fw" aria-hidden="true" title="Views"></span>
                                        <span>{{ comic.views }}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div v-if="comic.status" class="row border-bottom py-1">
                            <div class="col-md-2 fw-bold">
                                <label class="mb-0 label-badge">{{ reader.__('Status') }}:</label>
                            </div>
                            <div class="col-md-10">
                                <span class="badge badge-status p-2 text-white" title="Status" :data-status="comic.status ? comic.status.toLowerCase() : ''">{{ comic.status }}</span>
                            </div>
                        </div>

                        <div v-if="comic.description" class="row py-1">
                            <div class="col-md-2 fw-bold">
                                <label class="mb-0">{{ reader.__('Description') }}:</label>
                            </div>
                            <div class="col-md-10 text-pre text-justify">{{ comic.description }}</div>
                        </div>

                    </div>
                </div>
            </div>
        </div>

        <div v-if="comic.characters && comic.characters.length" class="card mt-3" id="comic-characters">
            <div class="card-header">
                <div class="row">
                    <div class="col-9 pt-2">
                        <span class="fas fa-users fa-fw" aria-hidden="true"></span> Personnages
                    </div>
                    <div class="col-3 text-end text-muted small pt-3">{{ comic.characters.length }}</div>
                </div>
            </div>
            <div class="card-body">
                <div class="mc-characters">
                    <div v-for="character in visibleCharacters" :key="character.name" class="mc-character">
                        <img v-if="character.image" :src="character.image" :alt="character.name" loading="lazy" decoding="async">
                        <span v-else class="mc-character-placeholder" aria-hidden="true"><span class="fas fa-user"></span></span>
                        <span class="mc-character-body">
                            <strong :title="character.name">{{ character.name }}</strong>
                            <small v-if="character.role">{{ character.role }}</small>
                        </span>
                    </div>
                </div>
                <button v-if="comic.characters.length > charactersLimit" type="button" class="mc-characters-more" @click="toggleCharacters">
                    <span class="fas fa-fw" :class="charactersExpanded ? 'fa-minus' : 'fa-plus'" aria-hidden="true"></span>
                    {{ charactersExpanded ? 'Voir moins' : 'Voir plus (' + (comic.characters.length - charactersLimit) + ')' }}
                </button>
            </div>
        </div>

        <div id="comic_top_html" class="text-center"></div>

        <div class="card mt-3">
            <div class="card-header">
                <div class="row">
                    <div class="col-sm-9 pt-2">
                        <span class="fas fa-book-open fa-fw"></span>
                        {{ reader.__('Chapters') }}
                    </div>
                    <div class="col-sm-3">
                        <input class="form-control me-sm-2 ui-autocomplete-input card-search" type="search"
                               :placeholder="reader.__('Filter chapters')" aria-label="Filter" name="filter" autocomplete="off">
                    </div>
                </div>
            </div>
            <div class="card-body mc-chapter-grid">

                <div v-if="false" class="row border-bottom py-1 d-sm-flex d-none">
                    <div class="col-sm-auto text-center pe-0">
                        <span class="fa fa-eye fa-fw" title="Read"></span>
                        <span class="fa fa-download fa-fw ps-sm-1" title="Direct download"></span>
                        <span class="fa fa-cloud-download-alt fa-fw" title="External download"></span>
                        <span class="fa fa-file-pdf fa-fw" title="PDF"></span>
                    </div>
                    <div class="col-sm-5 text-start">{{ reader.__('Title') }}</div>
                    <div class="col-sm-auto text-center">
                        <span class="fa fa-globe fa-fw" title="Language" style="width:24px"></span>
                    </div>
                    <div class="col-sm-2 text-success text-start ps-3">
                        <span class="fa fa-users fa-fw" title="Team"></span>
                    </div>
                    <div class="col-md-2 col text-info text-end"><span class="fa fa-eye fa-fw" title="Views"></span></div>
                    <div class="col-sm text-end ps-0"><span class="fa fa-clock fa-fw" title="Publication date"></span></div>
                </div>

                <div v-for="chapter in comic.chapters" :key="chapter.url"
                     :class="'mc-chapter-tile item' + (chapter.hidden ? ' hidden' : '') + (chapter.licensed ? ' licensed' : '') + (chapter.anime_resume ? ' anime-resume' : '') + (getViewed(chapter.slug_lang_vol_ch_sub) ? ' read text-secondary' : '')">
                    <div class="mc-chapter-actions">
                        <span :class="'cursor-pointer fa fa-eye' + (getViewed(chapter.slug_lang_vol_ch_sub) ? '' : '-slash') + ' fa-fw'"
                              :title="'Mark as ' + (getViewed(chapter.slug_lang_vol_ch_sub) ? 'unread' : 'read')" @click="setViewed(chapter.slug_lang_vol_ch_sub)"></span>
                        <a v-if="chapter.chapter_download !== null" :href="reader.API_BASE_URL + chapter.chapter_download">
                            <span class="fa fa-download fa-fw ps-sm-1" title="Direct download"></span>
                        </a>
                        <span v-else class="fa fa-download fa-fw text-secondary ps-sm-1" title="Direct download unavailable"></span>
                        <a v-if="chapter.download_link !== null" :href="chapter.download_link" target="_blank">
                            <span class="fa fa-cloud-download-alt fa-fw" title="External download"></span>
                        </a>
                        <span v-else class="fa fa-cloud-download-alt fa-fw text-secondary" title="External download unavailable"></span>
                        <a v-if="chapter.pdf !== null" :href="reader.API_BASE_URL + chapter.pdf">
                            <span class="fa fa-file-pdf fa-fw" title="PDF"></span>
                        </a>
                        <span v-else class="fa fa-file-pdf fa-fw text-secondary" title="PDF unavailable"></span>
                    </div>
                    <div class="mc-chapter-title">
                        <span class="chapter">
                            <a v-if="chapter.id !== null" :href="reader.BASE_URL + 'admin/comics/' + comic.slug + '/chapters/' + chapter.id" target="_blank">
                                <span class="fas fa-edit fa-fw me-0" aria-hidden="true" title="Edit"></span>
                            </a>
                            <router-link v-if="!chapter.licensed || chapter.id !== null" :to="chapter.url" class="filter" :title="chapter.full_title"><strong>{{ chapter.chapter != null ? 'Chapitre ' + chapter.chapter + (chapter.subchapter != null ? '.' + chapter.subchapter : '') : 'One-shot' }}</strong><small>{{ chapter.title || chapter.full_title }}</small></router-link>
                            <span v-else data-bs-toggle="modal" data-bs-target="#modal-container" class="filter" :title="chapter.full_title + ' [LICENSED]'">{{ chapter.full_title }}</span>
                            <span v-if="getViewed(chapter.slug_lang_vol_ch_sub)" class="mc-chapter-read" title="Chapitre lu"><span class="fas fa-check" aria-hidden="true"></span> Lu</span>
                            <span v-if="chapter.anime_resume" class="mc-chapter-resume" title="Reprendre la lecture ici après l'animé"><span class="fas fa-tv fa-fw" aria-hidden="true"></span> Reprendre après l'animé</span>
                        </span>
                    </div>
                    <div class="mc-chapter-teams"
                         :title="chapter.teams.filter(Boolean).map(function(el){return el.name;}).join(', ')">
                        <template v-if="chapter.teams[0] != null">
                            <a :href="chapter.teams[0].url" target="_blank">{{ chapter.teams[0].name }}</a>
                        </template>
                        <template v-if="chapter.teams[1] != null">,&nbsp;
                            <a :href="chapter.teams[1].url" target="_blank">{{ chapter.teams[1].name }}</a>
                        </template>
                    </div>
                    <div class="mc-chapter-views" title="Vues"><span class="fas fa-eye" aria-hidden="true"></span> {{ chapter.views }}</div>
                    <router-link class="mc-chapter-comments" :class="{'is-empty': !chapter.comments_count}"
                                 :to="chapter.url + '?comments=1'"
                                 :title="chapter.comments_count ? chapter.comments_count + ' commentaire' + (chapter.comments_count > 1 ? 's' : '') : 'Ajouter un commentaire'">
                        <span class="fas fa-comments fa-fw" aria-hidden="true"></span> <strong>{{ chapter.comments_count || 0 }}</strong>
                    </router-link>
                    <div class="mc-chapter-time ch-time" :title="new Date(chapter.published_on)">
                        {{ (chapter.time || '').replace(/(\d)\s*d\b/, '$1 j') }}
                    </div>
                </div>

            </div>
        </div>

        <div id="comic_bottom_html" class="text-center"></div>

        <div v-if="Object.keys(comic.volume_downloads).length" class="card mt-3">
            <div class="card-header">
                <div class="row">
                    <div class="col-sm-12 pt-2">
                        <span class="fas fa-book-reader fa-fw"></span>
                        {{ reader.__('Volume downloads') }}
                    </div>
                </div>
            </div>
            <div class="card-body">

                <div class="row border-bottom py-1 d-sm-flex d-none">
                    <div class="col-sm-auto text-center pe-0">
                        <span class="fa fa-download fa-fw ps-sm-1" title="Direct download"></span>
                    </div>
                    <div class="col-10 text-start">{{ reader.__('Filename') }}</div>
                </div>

                <div v-for="(download, volume) in comic.volume_downloads" class="row flex-sm-nowrap text-truncate border-bottom py-1 item">
                    <div class="col-auto text-sm-center pe-0 order-0 overflow-hidden">
                        <a :href="reader.API_BASE_URL + download">
                            <span class="fa fa-download fa-fw ps-sm-1" title="Direct download"></span>
                        </a>
                    </div>
                    <div class="col-10 text-start order-1 overflow-hidden">
                        <span class="chapter">
                            <a :href="reader.API_BASE_URL + download">{{ volume }}</a>
                        </span>
                    </div>
                </div>

            </div>
        </div>
        <popup :popupData="popupData" ></popup>
    </div>
    <div v-else ref="notfound"></div>
</template>

<script>
import Vue from 'vue'
import {mapGetters} from "vuex";
import NotFound from './NotFound.vue';
import Popup from'./Popup.vue';

export default {
    name: "Comic",
    components : { NotFound, Popup },
    mounted() {
        $('body').removeClass('body-reader hide-header');
        $('#nav-search').show();
        $('#nav-filter').hide();

        this.$store.dispatch('fetchComic', this.$route)
            .then(() => {
                if (this.$store.getters.comic === null) {
                    const ComponentClass = Vue.extend(NotFound);
                    const instance = new ComponentClass({
                        propsData: { reader: this.reader }
                    });
                    instance.$mount();
                    if(typeof this.$refs.notfound !== "undefined") this.$refs.notfound.appendChild(instance.$el);
                    const title = 'Error 404' + " | " + this.reader.SITE_NAME;
                    $('title').html(title);
                    $('meta[property="og:title"]').html(title);
                } else {
                    const title = this.$store.getters.comic.title + " | " + this.reader.SITE_NAME;
                    $('title').html(title);
                    $('meta[property="og:title"]').html(title);
                }
                filter_search();
            });
        this.comic != null && this.updateCustomHTML();
        $('html,body').scrollTop(0);
    },
    updated() {
        this.comic != null && this.updateCustomHTML();
    },
    data() {
        return {
            reader: this.$root,
            viewed: this.$root.getSetting('viewed') ? JSON.parse('' + this.$root.getSetting('viewed')) : {},
            popupData : {
                "header" : this.$root.__('Chapter licensed'),
                "body" : this.$root.__('This chapter is licensed and you can\'t read it.'),
                "button" : "Ok",
            },
            ratingHover: 0,
            ratingBusy: false,
            ratingThanks: false,
            charactersExpanded: false
        }
    },
    methods: {
        getViewed(slug) {
            if(typeof this.viewed[this.$store.getters.comic.slug] === 'undefined') {
                return 0;
            }
            return this.viewed[this.$store.getters.comic.slug][slug] || 0;
        },
        setViewed(slug) {
            if(typeof this.viewed[this.$store.getters.comic.slug] === 'undefined'){
                this.viewed[this.$store.getters.comic.slug] = {};
            }
            this.viewed[this.$store.getters.comic.slug][slug] ^= 1;
            localStorage.setItem('viewed', JSON.stringify(this.viewed));
            //this.reader.setCookie('viewed', JSON.stringify(this.viewed), 3650);
            this.$forceUpdate();
        },
        starState(n) {
            const info = this.ratingInfo;
            const value = this.ratingHover > 0 ? this.ratingHover : (info && info.your_vote ? info.your_vote : (info && info.average ? info.average : 0));
            if (n <= Math.floor(value)) return 'full';
            if (n === Math.ceil(value) && value % 1 >= 0.25) return 'half';
            return 'empty';
        },
        starIcon(n) {
            const state = this.starState(n);
            return state === 'full' ? 'fas fa-star' : (state === 'half' ? 'fas fa-star-half-stroke' : 'far fa-star');
        },
        sendRating(n) {
            const slug = this.comic ? this.comic.slug : null;
            if (!slug || this.ratingBusy) return;
            this.ratingBusy = true;
            this.$store.dispatch('sendRating', {slug: slug, vote: n})
                .then(() => {
                    this.ratingThanks = true;
                    setTimeout(() => { this.ratingThanks = false; }, 5000);
                })
                .catch(() => {})
                .finally(() => { this.ratingBusy = false; });
        },
        toggleFollow() {
            if (this.comic) this.$store.dispatch('toggleFollow', this.comic.slug);
        },
        toggleCharacters() {
            this.charactersExpanded = !this.charactersExpanded;
            if (!this.charactersExpanded) {
                const card = document.getElementById('comic-characters');
                if (card) window.scrollTo({ top: card.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' });
            }
        },
        updateCustomHTML() {
            this.reader.updateCustomHTML('comic_top_html');
            this.reader.updateCustomHTML('comic_bottom_html');
        },
    },
    watch: {
        comic: {
            immediate: true,
            handler(value) {
                if (value && value.slug) this.$store.dispatch('fetchRating', value.slug);
            }
        }
    },
    computed: {
        ...mapGetters([
            'comic',
            'isFollowed'
        ]),
        ratingInfo() {
            return this.$store.getters.rating(this.comic ? this.comic.slug : null);
        },
        ratingLabel() {
            const info = this.ratingInfo;
            if (!info || info.average === null || info.average === undefined) return '';
            const value = Number(info.average);
            return (Number.isInteger(value) ? String(value) : value.toFixed(1).replace('.', ',')) + '/5';
        },
        charactersLimit() {
            return 6;
        },
        visibleCharacters() {
            const characters = (this.comic && this.comic.characters) || [];
            return this.charactersExpanded ? characters : characters.slice(0, this.charactersLimit);
        }
    }
}
</script>

<style>
#comic .mc-follow-btn { float: right; display: inline-flex; align-items: center; gap: 5px; margin-top: 1px; padding: 3px 13px; border: 1px solid #d8d3e8; border-radius: 999px; background: #fff; color: #5b5570; font-size: 12px; font-weight: 700; line-height: 1.6; cursor: pointer; transition: border-color .15s, color .15s, background .15s; }
#comic .mc-follow-btn .fas, #comic .mc-follow-btn .far { font-size: 11px; }
#comic .mc-follow-btn:hover { border-color: #7055d8; color: #7055d8; }
#comic .mc-follow-btn.is-followed { background: #7055d8; border-color: #7055d8; color: #fff; }
#comic .mc-follow-btn.is-followed:hover { background: #6045c8; border-color: #6045c8; }
.dark #comic .mc-follow-btn { background: #1c1e20; border-color: #3a3548; color: #b9b4c9; }
.dark #comic .mc-follow-btn:hover { border-color: #a391f8; color: #a391f8; }
.dark #comic .mc-follow-btn.is-followed { background: #7055d8; border-color: #7055d8; color: #fff; }
#comic .mc-chapter-read { display: inline-flex; align-items: center; gap: 3px; margin: 3px 0 0; padding: 1px 7px; border-radius: 999px; background: #ebe7f7; color: #6b5fb5; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; vertical-align: 1px; }
.dark #comic .mc-chapter-read { background: #2c2939; color: #b3a5ef; }
#comic .mc-chapter-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 10px; }
.mc-chapter-tile { position: relative; display: grid; grid-template-columns: 1fr auto; gap: 4px; min-width: 0; padding: 10px; line-height: 1.35; border-radius: 5px; background: #f1f1f6; color: #33343f; }
.dark .mc-chapter-tile { background: #1c1e20; color: #d4d6dc; }
.mc-chapter-title { grid-column: 1; grid-row: 1; min-width: 0; }
.mc-chapter-title a.filter { color: inherit; text-decoration: none; }
.mc-chapter-title a.filter::after { content: ''; position: absolute; inset: 0; border-radius: 5px; }
.mc-chapter-title strong, .mc-chapter-title small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mc-chapter-title strong { font-size: 14px; font-weight: 800; }
.mc-chapter-title small { margin-top: 3px; font-size: 11px; opacity: .65; }
.mc-chapter-title a:not(.filter), .mc-chapter-actions, .mc-chapter-teams a { position: relative; z-index: 1; }
.mc-chapter-views { grid-column: 2; grid-row: 1; font-size: 12px; padding-top: 3px; white-space: nowrap; }
.mc-chapter-comments { grid-column: 2; grid-row: 2; justify-self: end; align-self: start; display: inline-flex; align-items: center; gap: 3px; margin-top: 2px; font-size: 12px; font-weight: 800; color: #7a63d6; text-decoration: none; position: relative; z-index: 1; }
.mc-chapter-comments:hover { text-decoration: underline; }
.mc-chapter-comments.is-empty { opacity: .45; font-weight: 600; }
.dark .mc-chapter-comments { color: #b3a5ff; }
.mc-chapter-actions { grid-column: 1; grid-row: 3; display: flex; gap: 7px; align-items: center; align-self: end; font-size: 12px; }
.mc-chapter-actions a { color: inherit; }
.mc-chapter-teams { grid-column: 1; grid-row: 2; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 10px; opacity: .65; }
.mc-chapter-time { grid-column: 2; grid-row: 3; justify-self: end; align-self: end; transform: translate(4px, 4px); font-size: 10px; opacity: .65; white-space: nowrap; }
.mc-chapter-tile:hover { box-shadow: inset 0 0 0 1px #8f9aff; }
.mc-chapter-tile.read { opacity: .65; }
.mc-chapter-tile.anime-resume { background: #fff6d8; box-shadow: inset 0 0 0 2px #f0b429; }
.dark .mc-chapter-tile.anime-resume { background: #2c2617; }
.mc-chapter-tile.anime-resume.read { opacity: 1; }
.mc-chapter-resume { display: inline-flex; align-items: center; gap: 4px; margin-top: 5px; font-size: 10px; font-weight: 700; color: #a1690a; }
.dark .mc-chapter-resume { color: #f0b429; }
@media (max-width: 1199px) { #comic .mc-chapter-grid { grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); } }
@media (max-width: 767px) { #comic .mc-chapter-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; } }
@media (max-width: 479px) { #comic .mc-chapter-grid { grid-template-columns: minmax(0, 1fr); } }
</style>
